import {
  assetConditions,
  assetConditionsBySeverity,
} from '@/domain/enums/assetCondition';
import {
  alarmCategories,
  alarmClassifications,
} from '@/domain/enums/alarmCategory';

/**
 * Situações intencionais: o Sensor está fora do ar porque alguém o desligou, e não
 * porque falhou.
 */
export const NEUTRAL_COUNTS = ['disabled', 'underMaintenance'];

/**
 * Status de Sensor que conta como monitoramento de pé — mesmo o que comunica mal.
 *
 * A qualidade da comunicação **não é assunto desta tela**: ela se lê no Painel de
 * Status de Comunicação. Aqui só interessa a pergunta grossa: existe algum
 * monitoramento ligado neste Ativo? Ver
 * docs/adr/0013-mapa-de-atencao-sem-comunicacao.md
 */
export const OPERATING_COUNTS = [
  'active',
  'simulation',
  'inactive',
  'indeterminate',
  'establishingCommunication',
  'partialCommunication',
];

/** Todos os status: a soma é o total de Sensores do Ativo. */
export const ALL_COUNTS = OPERATING_COUNTS.concat(NEUTRAL_COUNTS);

function countOf(counts, keys) {
  return keys.reduce((total, key) => total + (Number((counts || {})[key]) || 0), 0);
}

/**
 * Diz se o Ativo está **Fora de Operação**: nenhum Sensor com monitoramento
 * ligado, seja porque todos foram desabilitados ou postos em manutenção, seja
 * porque o Ativo não tem Sensor nenhum.
 *
 * É a mesma regra do Painel de Status de Comunicação, e de propósito: dois painéis
 * do mesmo menu não podem chamar a mesma situação por nomes diferentes nem chegar
 * a respostas diferentes. Ver
 * docs/adr/0001-estado-do-ativo-por-pior-vence-com-estados-neutros.md
 *
 * @param {Object} counts Contagem por status, como o endpoint devolve.
 * @returns {Boolean} Verdadeiro quando ninguém está medindo o Ativo.
 */
export function isOutOfService(counts) {
  return !countOf(counts, OPERATING_COUNTS);
}

/**
 * Origens que a tela conta. *Rede Digital* nunca entra: comunicação não é assunto
 * daqui. Ver docs/adr/0013-mapa-de-atencao-sem-comunicacao.md
 */
export const countedOrigins = [
  alarmCategories.ALARM,
  alarmCategories.SELF_DIAGNOSIS,
];

/**
 * As classificações que um Alarme pode ter. São três, e não quatro: o
 * `OBSERVATION` do enum `AlarmClassificationType` não é classificação de alarme —
 * ele existe porque o enum espelha `ClassificationType`, onde Observação é
 * classificação de **diagnóstico** de cromatografia. Nenhuma tela desta aplicação
 * oferece Observação ao classificar um alarme.
 */
export const countedClassifications = [
  alarmClassifications.CRITICAL,
  alarmClassifications.URGENT,
  alarmClassifications.ORDINARY,
];

/** Matriz zerada, para o Ativo sem alarme ter a mesma forma dos demais. */
function emptyActedAlarms() {
  return countedOrigins.reduce((matrix, origin) => ({
    ...matrix,
    [origin]: countedClassifications.reduce(
      (row, classification) => ({ ...row, [classification]: 0 }),
      {},
    ),
  }), {});
}

/**
 * Traduz a contagem do endpoint na matriz Origem × Classificação.
 *
 * Toda célula existe, mesmo zerada: quem lê a matriz não precisa testar ausência
 * a cada acesso, e um campo que o backend deixe de mandar vira zero em vez de
 * `undefined` se espalhando pelas contas.
 *
 * @param {Object} counts `alarmesAtuados` como o endpoint devolve.
 * @returns {Object} Matriz `{ [origem]: { [classificacao]: número } }`.
 */
export function buildActedAlarms(counts) {
  const source = {
    [alarmCategories.ALARM]: (counts || {}).alarm,
    [alarmCategories.SELF_DIAGNOSIS]: (counts || {}).selfDiagnosis,
  };

  return countedOrigins.reduce((matrix, origin) => ({
    ...matrix,
    [origin]: countedClassifications.reduce((row, classification) => ({
      ...row,
      [classification]: Number((source[origin] || {})[classification]) || 0,
    }), {}),
  }), emptyActedAlarms());
}

/**
 * Quantos Alarmes Atuados o Ativo tem no recorte pedido.
 *
 * Sem recorte, soma a matriz inteira. Com recorte, soma só as células cruzadas —
 * é isso que faz "Crítico **e** Autodiagnóstico" significar *um alarme que é as
 * duas coisas*, e não *um crítico qualquer mais um autodiagnóstico qualquer*.
 *
 * @param {Object} actedAlarms Matriz do Ativo.
 * @param {String[]} [origins] Origens pedidas; vazio vale por todas.
 * @param {String[]} [classifications] Classificações pedidas; vazio vale por todas.
 * @returns {Number} Soma das células.
 */
export function countActedAlarms(actedAlarms, origins, classifications) {
  const rows = (origins || []).length ? origins : countedOrigins;
  const columns = (classifications || []).length ? classifications : countedClassifications;

  return rows.reduce((total, origin) => columns.reduce(
    (sum, classification) => sum + (((actedAlarms || {})[origin] || {})[classification] || 0),
    total,
  ), 0);
}

/**
 * Condição do Ativo: a contagem de Alarmes Atuados ou, sem nenhum, o
 * monitoramento.
 *
 * Três regras, e cada uma já foi motivo de engano aqui:
 *
 * 1. **Autodiagnóstico nunca é Crítico**, qualquer que seja a classificação dele.
 *    O vermelho fica reservado a problema no Ativo, não no Sensor — e é por isso
 *    que só a linha de Alarme é consultada para o vermelho. Um Ativo só com
 *    autodiagnósticos críticos sai daqui como Atenção.
 * 2. **Pendente não conta.** Alarme que atuou, normalizou e ninguém reconheceu
 *    deixa o Ativo Normal: o evento acabou, e o que falta é o reconhecimento.
 *    Ele continua na lista e ganha a marca de pendência. Ver
 *    docs/adr/0014-condicao-do-ativo-so-conta-alarme-atuado.md
 * 3. **Alarme vence Fora de Operação.** Um Ativo com crítico e monitoramento
 *    desligado sai como Crítico: um painel de vigilância não esconde um crítico
 *    atrás de cinza.
 *
 * Ver docs/adr/0009-condicao-do-ativo-separada-do-estado-do-ativo.md
 *
 * @param {Object} actedAlarms Matriz de Alarmes Atuados do Ativo.
 * @param {Boolean} outOfService Ativo sem monitoramento algum ligado.
 * @returns {String} Um valor de `assetConditions`.
 */
export function getAssetCondition(actedAlarms, outOfService) {
  const critical = countActedAlarms(
    actedAlarms,
    [alarmCategories.ALARM],
    [alarmClassifications.CRITICAL],
  );

  if (critical) {
    return assetConditions.CRITICAL;
  }

  if (countActedAlarms(actedAlarms)) {
    return assetConditions.ATTENTION;
  }

  return outOfService ? assetConditions.OUT_OF_SERVICE : assetConditions.NORMAL;
}

/**
 * Consolida várias condições numa só, pela mais grave. É a cor do Agrupamento do
 * Mapa.
 *
 * @param {String[]} conditions Condições a consolidar.
 * @returns {String} A condição mais grave, ou Normal se a lista é vazia.
 */
export function getWorstCondition(conditions) {
  const found = assetConditionsBySeverity.filter(
    (condition) => (conditions || []).indexOf(condition) >= 0,
  );

  return found.length ? found[0] : assetConditions.NORMAL;
}

function buildAsset(installation, asset) {
  const actedAlarms = buildActedAlarms(asset.actedAlarms);

  return {
    key: asset.assetId,
    name: asset.name,
    type: asset.assetType,
    company: installation.company,
    companyId: installation.companyId,
    installation: installation.installation,
    installationId: installation.installationId,
    regional: installation.regional,
    regionalId: installation.regionalId,
    latitude: installation.latitude,
    longitude: installation.longitude,
    condition: getAssetCondition(actedAlarms, isOutOfService(asset.moduleStatusCount)),
    actedAlarms,
    // A pendência não entra na matriz nem na cor: ela é a única coisa que o
    // Ativo tem a dizer quando está verde e mesmo assim deve algo a alguém.
    pendingAlarm: (Number(asset.pendingAlarmCount) || 0) > 0,
    pendingAlarmCount: asset.pendingAlarmCount || 0,
    sensorCount: countOf(asset.moduleStatusCount, ALL_COUNTS),
  };
}

/**
 * Planifica o retorno do endpoint em Ativos, que são a unidade da tela.
 *
 * @param {Object[]} installations Instalações vindas de `/Alarme/mapa-atencao`.
 * @returns {Object[]} Ativos com Condição, comunicação e coordenada da instalação.
 */
export function buildAssets(installations) {
  return (installations || []).reduce(
    (assets, installation) => assets.concat(
      (installation.assets || []).map((asset) => buildAsset(installation, asset)),
    ),
    [],
  );
}

/**
 * Placar da tela: a frota inteira do escopo, por Condição.
 *
 * Conta sempre o conjunto completo, e não o recorte do filtro — é a referência
 * contra a qual o recorte é lido.
 */
export function summarizeAssets(assets) {
  const list = assets || [];
  const countBy = (condition) => list.filter(
    (asset) => asset.condition === condition,
  ).length;

  const installations = list.reduce((ids, asset) => {
    if (ids.indexOf(asset.installationId) < 0) {
      ids.push(asset.installationId);
    }

    return ids;
  }, []);

  return {
    assetCount: list.length,
    installationCount: installations.length,
    sensorCount: list.reduce((total, asset) => total + (asset.sensorCount || 0), 0),
    critical: countBy(assetConditions.CRITICAL),
    attention: countBy(assetConditions.ATTENTION),
    normal: countBy(assetConditions.NORMAL),
    outOfService: countBy(assetConditions.OUT_OF_SERVICE),
  };
}
